/**
 * Right to erasure (PRD 11.2): "PII fields can be zeroed while preserving
 * audit trail structure -- value replaced by '[REDACTED -- erasure request
 * #XXXXX]'".
 *
 * Every private value recorded about a person is replaced, card by card, with
 * a marker naming the request, and each replacement is its own row in the
 * log. Nothing else moves: the cards keep their states, the log keeps every
 * row it had (none of which ever held a private value), and a card that read
 * the value through a data link now reads the marker. Erasing works on the
 * instance as stored, values still sealed, so it never opens one.
 */

import type { Actor, AuditEvent, Outcome } from "./engine.js";
import type { Instance } from "./instance.js";

const MARK = "[REDACTED — erasure request #";

/** What a private value becomes. Not itself private, so it is stored as it is, not sealed. */
export const redaction = (reference: string): string => `${MARK}${reference}]`;

export const isRedacted = (value: string): boolean => value.startsWith(MARK) && value.endsWith("]");

/** Admins and compliance officers: the people who answer for the record, as with exporting it. */
export const canErase = (actor: Pick<Actor, "role">): boolean =>
  actor.role === "admin" || actor.role === "compliance";

/** The request's own number or reference, as the organisation tracks it: "#2026-014", "DSR 88". */
export function erasureReference(
  raw: unknown,
): { ok: true; reference: string } | { ok: false; reason: string } {
  const reference = typeof raw === "string" ? raw.trim().replace(/^#\s*/, "") : "";
  if (!reference)
    return { ok: false, reason: "Give the erasure request's number, so the log says which request it was." };
  if (reference.length > 40 || !/^[\w .\/-]+$/.test(reference))
    return {
      ok: false,
      reason: "Keep the request's number to 40 letters, digits, spaces, dots, dashes and slashes.",
    };
  return { ok: true, reference };
}

/** Private values recorded on an instance and not yet erased, for saying what an erasure would do. */
export function privateValueCount(instance: Instance): number {
  let count = 0;
  for (const key of instance.order)
    for (const field of instance.nodes[key]?.fields ?? []) {
      const value = instance.tasks[key]?.values[field.key];
      if (field.pii && value && !isRedacted(value)) count += 1;
    }
  return count;
}

/**
 * Replace every private value on one instance with the request's marker.
 * Finished cards included: erasure is not a change to the work, and a card's
 * finishing is exactly what the log keeps. No notices: nobody's work moved.
 */
export function erase(instance: Instance, reference: string, input: { actor: Actor; now: number }): Outcome {
  const { actor, now } = input;
  if (!canErase(actor))
    return { ok: false, reason: "Only an admin or a compliance officer can erase private values." };
  const marker = redaction(reference);
  const tasks = { ...instance.tasks };
  const events: AuditEvent[] = [];
  for (const key of instance.order) {
    const task = tasks[key];
    if (!task) continue;
    let values: Record<string, string> | null = null;
    for (const field of instance.nodes[key]?.fields ?? []) {
      const value = task.values[field.key];
      if (!field.pii || !value || isRedacted(value)) continue;
      values ??= { ...task.values };
      values[field.key] = marker;
      events.push({
        at: now,
        actor: actor.id,
        action: "erase",
        task: key,
        from: "[PII]",
        to: marker,
        note: field.label,
      });
    }
    if (values) tasks[key] = { ...task, values };
  }
  return { ok: true, instance: { ...instance, tasks }, events, effects: [] };
}
